import { useGetProjectTestSessionQuery } from '../hooks/bug-management/useProjectFindings'
import { journeyStopReason } from '../lib/journeyToolFailure'
import JourneySteps from './JourneySteps'
import { useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Link } from './nav'
import { RefreshCw, Route, Smartphone, Sparkles, ClipboardList, ArrowUpRight, History, GitBranch, Clock, Globe, LogIn, Hash, Play, CalendarDays, UserRound } from 'lucide-react'
import type { Journey, JourneyVersion, TestRun } from '../data/projectTypes'
import {

  useGetChildJourneysQuery,
  useGetJourneyQuery,
  useGetJourneyTestRunsQuery,
  useGetProjectQuery,
} from '../services/projectDataApi'
import {
  JourneyStatusIcon,
  JourneySourceLink,
  JourneyBugBanner,
} from './JourneysTab'
import JourneyReplayHero from './JourneyReplayHero'
import StatusIndicator from './StatusIndicator'
import BackLink, { type BackTarget } from './BackLink'
import TargetUrlBadge from './TargetUrlBadge'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { pickTargetUrl } from '../lib/targetUrl'
import { projectPause, type ProjectPause } from '../lib/projectPause'
import { useViewTransitionNavigate, isPlainLeftClick } from '../lib/viewTransitionNav'
import { useAdminMode } from '../hooks/useAdminMode'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import { isUnauthorizedError } from '../lib/unauthorizedError'
import { displayRunStatus } from '../guidance/journey/run-status'
import '../pages/JourneyDetail.css'

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

function runSortKey(run: TestRun): number {
  return new Date(run.created_at || run.started_at).getTime()
}

// A single test run row, linking to the task-detail page. The task route exists in both trees, and
// the embed-aware Link keeps the target inside `/p/*` when this renders in the embed.
function VersionRunRow({ projectId, run, pause }: { projectId: string; run: TestRun; pause: ProjectPause | null }) {
  // Test-run rows open the admin-only task-detail page; for non-admins they render as static rows.
  const adminMode = useAdminMode()
  // `bugs_found_count` can lag behind the actual bugs filed (e.g. a bug rejected by the judge still
  // appears in `bug_titles`), so fall back to the title list to avoid claiming "No bugs" wrongly.
  const bugCount = run.bugs_found_count > 0 ? run.bugs_found_count : (run.bug_titles?.length ?? 0)
  const inner = (
    <>
      <StatusIndicator status={displayRunStatus(run.status, run.outcome_status)} pause={pause} />
      <span className="journey-detail__run-time">
        <Clock size={12} aria-hidden /> {formatDate(run.created_at || run.started_at)}
      </span>
      <span className="journey-detail__run-bugs">
        {bugCount > 0 ? `${bugCount} ${bugCount === 1 ? 'bug' : 'bugs'}` : 'No bugs'}
      </span>
      {adminMode && <ArrowUpRight size={14} aria-hidden className="journey-detail__run-arrow" />}
    </>
  )
  if (!adminMode) {
    return (
      <div className="journey-detail__run-row journey-detail__run-row--static" data-testid={`journey-run-${run.id}`}>
        {inner}
      </div>
    )
  }
  return (
    <Link
      to={taskDetailUrl(projectId, run.id)}
      className="journey-detail__run-row"
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`journey-run-${run.id}`}
      data-analytics-event="journey-run-link"
    >
      {inner}
    </Link>
  )
}

function VersionCard({
  projectId,
  version,
  runs,
  isLatest,
  pause,
}: {
  projectId: string
  version: JourneyVersion
  runs: TestRun[]
  isLatest: boolean
  pause: ProjectPause | null
}) {
  // The "Created by Exploration" link opens the admin-only task-detail page.
  const adminMode = useAdminMode()
  return (
    <div className="journey-detail__version" data-testid={`journey-version-${version.id}`}>
      <div className="journey-detail__version-header">
        <span className="journey-detail__version-num">
          <GitBranch size={14} aria-hidden /> Version {version.version_number}
          {isLatest && <span className="journey-detail__version-latest">Latest</span>}
        </span>
        <span className="journey-detail__version-meta">
          <JourneyStatusIcon status={displayRunStatus(version.last_run_status, version.last_run_outcome_status)} pause={pause} />
          <span className="journey-detail__version-time">
            <Clock size={12} aria-hidden /> Created {formatDate(version.created_at)}
          </span>
        </span>
      </div>
      <div className="journey-detail__version-stats">
        <span>{version.step_count ?? 0} steps</span>
        <span>{version.run_count} {version.run_count === 1 ? 'run' : 'runs'}</span>
        {version.last_run_at && <span>Last run {formatDate(version.last_run_at)}</span>}
      </div>
      {version.exploration_id && (
        <div className="journey-detail__version-source">
          <ArrowUpRight size={13} aria-hidden /> Created by{' '}
          {adminMode ? (
            <Link to={taskDetailUrl(projectId, version.exploration_id)} target="_blank" rel="noopener noreferrer" data-analytics-event="journey-version-source-link">Exploration</Link>
          ) : (
            <span>Exploration</span>
          )}
        </div>
      )}
      {runs.length > 0 ? (
        <div className="journey-detail__version-runs">
          {runs.map(run => (
            <VersionRunRow key={run.id} projectId={projectId} run={run} pause={pause} />
          ))}
        </div>
      ) : (
        <p className="journey-detail__version-no-runs">No test runs for this version yet.</p>
      )}
    </div>
  )
}

// One child-journey tile linking to that child's own journey page (within the same `basePath`
// context, so a `/p` view stays in `/p`). The link runs a view transition for continuity.
function ChildJourneyRow({ child, basePath, pause }: { child: Journey; basePath: string; pause: ProjectPause | null }) {
  const to = `${basePath}/journeys/${child.id}`
  const vtNavigate = useViewTransitionNavigate()
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!isPlainLeftClick(e)) return
    e.preventDefault()
    vtNavigate(to)
  }, [vtNavigate, to])
  return (
    <Link
      to={to}
      onClick={handleClick}
      className="journey-detail__child-row"
      data-testid={`journey-child-${child.id}`}
      data-analytics-event="journey-child-link"
    >
      <Route size={14} aria-hidden className="journey-detail__child-icon" />
      <span className="journey-detail__child-name">{child.name}</span>
      <JourneyStatusIcon status={displayRunStatus(child.last_run_status ?? null, child.last_run_outcome_status)} pause={pause} />
      <ArrowUpRight size={14} aria-hidden className="journey-detail__run-arrow" />
    </Link>
  )
}


interface JourneyDetailViewProps {
  projectId: string
  journeyId: string
  embedded: boolean
  /** Canonical base path without trailing slash, e.g. `/projects/proj-1`. The embed-aware Link /
   *  navigate primitives rewrite it to `/p/...` when rendering inside the iframe. */
  basePath: string
}

// Shared journey-detail body used by both the full app (`/projects/*`, wrapped in the AppShell
// sidebar layout) and the embedded `/p/*` view (chrome-free layout). The two routes differ only
// in the layout that wraps this component and in the `basePath`; every outbound link works in both
// trees thanks to the embed-aware Link.
export default function JourneyDetailView({ projectId, journeyId, basePath, embedded }: JourneyDetailViewProps) {
  const vtNavigate = useViewTransitionNavigate()
  const [searchParams] = useSearchParams()

  const projectQuery = useGetProjectQuery(projectId)
  const journeyQuery = useGetJourneyQuery(journeyId, {
    pollingInterval: 15_000,
    skipPollingIfUnfocused: true,
  })
  // A journey in a private project opened while signed out goes to sign-in and comes back here; the
  // skeleton body below covers the moment that takes. Keyed on the journey fetch, NOT the owner-only
  // runs fetch below — that one 401s for a legitimately anonymous viewer of a PUBLIC journey.
  useSignInOnUnauthorized(journeyQuery.error)
  const journey = journeyQuery.data
  const project = projectQuery.data
  // A paused project's last run never terminated: the journey, its versions and their run rows all
  // still say 'in-progress'. Every status on this page reads through the pause instead.
  const pause = projectPause(project)
  const childJourneysQuery = useGetChildJourneysQuery(
    { projectId, parentId: journeyId },
    { skip: !journey || journey.child_count === 0 },
  )
  const childJourneys = childJourneysQuery.data?.items
  const runsQuery = useGetJourneyTestRunsQuery(
    { projectId, journeyId, pageSize: 100 },
    { pollingInterval: 15_000, skipPollingIfUnfocused: true },
  )
  const runs = runsQuery.data
  // Distinguish authentication failures from network, server, and response-validation errors.
  const runsErrored = runsQuery.isError

  const testRunsPath = `${basePath}/test-runs`

  const ready = journey && journey.id === journeyId

  useEffect(() => {
    if (ready && journey) {
      document.title = `Replay QA: ${journey.name}`
    }
    return () => { document.title = 'Replay QA' }
  }, [ready, journey])

  const sortedRuns = useMemo(
    () => (runs ? [...runs].sort((a, b) => runSortKey(b) - runSortKey(a)) : []),
    [runs]
  )
  const latestRun = sortedRuns[0]
  // The run the cover/hero replays: the most recent *previewable* run (one with a recording, or one
  // still in-progress streaming live frames), falling back to the latest run. Mirrors the journeys
  // grid's preview_run_id so a newer failed/queued run doesn't replace a replayable earlier run with
  // an empty cover.
  const previewRun = sortedRuns.find(r => r.replay_recording_id || r.status === 'in-progress') ?? latestRun

  // A journey belongs to no single test run — it is exercised by one run per test session — so the
  // run this page came from is only knowable from `?run=` on the link that opened it.
  const fromSessionId = searchParams.get('run')
  const sessionsQuery = useGetProjectTestSessionQuery({ projectId, sessionId: fromSessionId ?? '' }, { skip: !fromSessionId })
  const fromSession = sessionsQuery.currentData?.session
  const sessionRun = fromSessionId
    ? sortedRuns.find(run => run.test_session_id === fromSessionId)
    : undefined
  // Back to that run when we came from one, otherwise to the run list — where the cover→grid morph
  // the old back button played still runs.
  const back: BackTarget = fromSessionId
    ? { label: fromSession?.title || 'Test Run', to: `${testRunsPath}/${encodeURIComponent(fromSessionId)}` }
    : {
        label: 'Test Runs',
        to: testRunsPath,
        onClick: (e) => { if (isPlainLeftClick(e)) { e.preventDefault(); vtNavigate(testRunsPath) } },
      }

  // Group runs under the version they exercised; runs without a known version go to a bucket.
  const runsByVersion = useMemo(() => {
    const map: Record<string, TestRun[]> = {}
    const orphans: TestRun[] = []
    for (const run of sortedRuns) {
      if (run.journey_version_id) {
        ;(map[run.journey_version_id] ??= []).push(run)
      } else {
        orphans.push(run)
      }
    }
    return { map, orphans }
  }, [sortedRuns])

  // Cover photo at the very top of the page: the latest replay, pinned like a banner. Its motion box
  // shares a `layoutId` with the journeys-grid card, so it MUST be mounted from the first paint —
  // even while the journey/runs are still loading — for the grid→cover open morph and the
  // lightbox→cover close morph to play. Earlier this lived deep inside a "Latest Run" section, so the
  // loading skeleton briefly replaced the morph target (breaking the open animation) and the close
  // animation landed in an arbitrary mid-page spot. Keeping the box always-mounted at the top, with a
  // placeholder when there's no replay yet, gives the shared element a stable target throughout.
  const heroSource = ready && previewRun ? { testRunId: previewRun.id } : null
  const heroTaskHref = previewRun ? taskDetailUrl(projectId, previewRun.id) : null
  let heroPlaceholder: React.ReactNode
  if (runsErrored) {
    heroPlaceholder = (
      <p className="journey-detail__cover-msg" data-testid={isUnauthorizedError(runsQuery.error) ? 'journey-detail-runs-restricted' : 'journey-detail-runs-error'}>
        {isUnauthorizedError(runsQuery.error)
          ? 'Sign in to load test runs and replays for this journey.'
          : 'Unable to load test runs and replays for this journey. Please try again.'}
      </p>
    )
  } else if (!ready || runs === undefined) {
    heroPlaceholder = <div className="journey-detail__cover-shimmer" aria-hidden />
  } else {
    heroPlaceholder = (
      <p className="journey-detail__cover-msg" data-testid="journey-detail-no-runs">
        No test runs yet — nothing to replay.
      </p>
    )
  }

  let body: React.ReactNode
  if (!ready || !journey) {
    body = (
      <>
        <div className="journey-detail__skeleton journey-detail__skeleton--title skeleton" />
        <div className="journey-detail__skeleton journey-detail__skeleton--block skeleton" />
      </>
    )
  } else {
    const versions = journey.versions ?? []
    const selectedRun = fromSessionId ? sessionRun : latestRun
    const latestVersion = selectedRun ? versions.find(version => version.id === selectedRun.journey_version_id) : versions[0]
    const latestSteps = latestVersion?.steps ?? journey.steps ?? null
    const latestStepCount = latestVersion?.step_count ?? journey.step_count ?? 0
    // When opened from a Test Run, show the URL that run actually exercised. A named environment
    // outranks the journey's legacy URL override; outside a run, preserve the journey/project fallback.
    const targetUrl = pickTargetUrl(
      sessionRun?.override_url,
      sessionRun?.environment_target_url,
      journey.target_url,
      project?.target_url,
      project?.github?.mainBranchDeploymentUrl,
    )
    const runCount = journey.test_run_count ?? sortedRuns.length
    const lastRunStatus = displayRunStatus(journey.last_run_status ?? null, journey.last_run_outcome_status)
    const header = (
      <header className="journey-detail__header">
        <div className="journey-detail__meta-line">
          <div className="journey-detail__navigation">
            <BackLink target={back} />
          </div>
          <div className="journey-detail__chips">
            {lastRunStatus
              ? <StatusIndicator status={lastRunStatus} pause={pause} withIcon />
              : <span className="journey-detail__chip">Never run</span>}
            {journey.mobile && (
              <span className="journey-detail__chip" data-testid="journey-detail-flag-mobile">
                <Smartphone size={13} aria-hidden /> Mobile
              </span>
            )}
            {journey.polish && (
              <span className="journey-detail__chip" data-testid="journey-detail-flag-polish">
                <Sparkles size={13} aria-hidden /> Polish
              </span>
            )}
            {journey.use_saved_session !== false && (
              <span className="journey-detail__chip" data-testid="journey-detail-flag-saved-session">
                <LogIn size={13} aria-hidden /> Starts signed in
              </span>
            )}
          </div>
        </div>
        <h1 className="journey-detail__title" data-testid="journey-detail-title">{journey.name}</h1>
      </header>
    )

    const rail = (
      <aside className="journey-detail__rail" aria-label="Journey details">
        <dl className="journey-detail__meta">
          {targetUrl && (
            <div className="journey-detail__meta-item">
              <Globe size={14} className="journey-detail__meta-icon" aria-hidden />
              <dt>Target</dt>
              <dd><TargetUrlBadge url={targetUrl} testId="journey-detail-target-url-badge" /></dd>
            </div>
          )}
          <div className="journey-detail__meta-item">
            <Hash size={14} className="journey-detail__meta-icon" aria-hidden />
            <dt>Steps</dt>
            <dd>{latestStepCount}</dd>
          </div>
          <div className="journey-detail__meta-item">
            <Play size={14} className="journey-detail__meta-icon" aria-hidden />
            <dt>Runs</dt>
            <dd>{runCount}</dd>
          </div>
          <div className="journey-detail__meta-item">
            <Clock size={14} className="journey-detail__meta-icon" aria-hidden />
            <dt>Last run</dt>
            <dd>{journey.last_run_at ? formatDate(journey.last_run_at) : '—'}</dd>
          </div>
          <div className="journey-detail__meta-item">
            <History size={14} className="journey-detail__meta-icon" aria-hidden />
            <dt>Versions</dt>
            <dd>{versions.length}</dd>
          </div>
          <div className="journey-detail__meta-item">
            <CalendarDays size={14} className="journey-detail__meta-icon" aria-hidden />
            <dt>Created</dt>
            <dd>{formatDate(journey.created_at)}</dd>
          </div>
          {latestVersion?.exploration_id && (
            <div className="journey-detail__meta-item journey-detail__meta-item--source">
              <ArrowUpRight size={14} className="journey-detail__meta-icon" aria-hidden />
              <dt>Source</dt>
              <dd><JourneySourceLink journey={journey} projectId={projectId} /></dd>
            </div>
          )}
        </dl>
      </aside>
    )

    body = (
      <div className="journey-detail__layout">
        <div className="journey-detail__main">
          {header}

          {journey.description && (
            <section className="journey-detail__card">
              <h2 className="journey-detail__card-title">Description</h2>
              <p className="journey-detail__desc">{journey.description}</p>
            </section>
          )}
          {journey.target_url && (
            <section className="journey-detail__card" data-testid="journey-detail-target-url">
              <h2 className="journey-detail__card-title"><Globe size={13} aria-hidden /> Custom URL</h2>
              <p className="journey-detail__desc">
                <a href={journey.target_url} target="_blank" rel="noopener noreferrer" data-analytics-event="journey-target-url-link">{journey.target_url}</a>
              </p>
            </section>
          )}
          {journey.instructions && (
            <section className="journey-detail__card" data-testid="journey-detail-instructions">
              <h2 className="journey-detail__card-title"><ClipboardList size={13} aria-hidden /> Special Instructions</h2>
              <p className="journey-detail__desc">{journey.instructions}</p>
            </section>
          )}
          {journey.source_session_id && (
            <section className="journey-detail__card" data-testid="journey-source-session">
              <h2 className="journey-detail__card-title"><UserRound size={13} aria-hidden /> Source user session</h2>
              <Link to={`${basePath}/user-sessions/${journey.source_session_id}?journey=${journey.id}`}>
                View session analysis <ArrowUpRight size={13} aria-hidden />
              </Link>
            </section>
          )}

          <JourneyBugBanner journey={journey} projectId={projectId} />

          {journey.child_count > 0 && (
            <section className="journey-detail__card" data-testid="journey-detail-children">
              <h2 className="journey-detail__card-title">
                <Route size={13} aria-hidden /> Child Journeys
                <span className="journey-detail__count">{journey.child_count}</span>
              </h2>
              {childJourneys ? (
                <div className="journey-detail__children">
                  {childJourneys.map(child => (
                    <ChildJourneyRow key={child.id} child={child} basePath={basePath} pause={pause} />
                  ))}
                </div>
              ) : (
                <p className="journey-detail__muted">Loading child journeys…</p>
              )}
            </section>
          )}

          {latestStepCount > 0 && (
            <JourneySteps
              projectId={projectId}
              steps={latestSteps}
              stepCount={latestStepCount}
              run={selectedRun}
              lastRunError={selectedRun ? journeyStopReason(selectedRun.outcome) : latestVersion?.last_run_error ?? null}
              lastRunStatus={selectedRun
                ? displayRunStatus(selectedRun.status, selectedRun.outcome_status)
                : fromSessionId ? null
                : latestVersion?.last_run_status != null
                  ? displayRunStatus(latestVersion.last_run_status, latestVersion.last_run_outcome_status)
                  : lastRunStatus}
            />
          )}

          <section className="journey-detail__card">
            <h2 className="journey-detail__card-title">
              <History size={13} aria-hidden /> Versions
              <span className="journey-detail__count">{versions.length}</span>
            </h2>
            {versions.length === 0 ? (
              <p className="journey-detail__muted">No versions recorded yet.</p>
            ) : (
              <div className="journey-detail__versions" data-testid="journey-detail-versions">
                {versions.map((version, idx) => (
                  <VersionCard
                    key={version.id}
                    projectId={projectId}
                    version={version}
                    runs={runsByVersion.map[version.id] ?? []}
                    isLatest={idx === 0}
                    pause={pause}
                  />
                ))}
                {runsByVersion.orphans.length > 0 && (
                  <div className="journey-detail__version" data-testid="journey-detail-other-runs">
                    <div className="journey-detail__version-header">
                      <span className="journey-detail__version-num">
                        <RefreshCw size={14} aria-hidden /> Other runs
                      </span>
                    </div>
                    <div className="journey-detail__version-runs">
                      {runsByVersion.orphans.map(run => (
                        <VersionRunRow key={run.id} projectId={projectId} run={run} pause={pause} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
        {rail}
      </div>
    )
  }

  return (
    <div
      className={`journey-detail${embedded ? '' : ' journey-detail--replay-header'}`}
      data-testid={ready ? 'journey-detail-page' : 'journey-detail-loading'}
    >
      {/* Cover photo: the latest replay, pinned to the top like a banner. Shares a `layoutId` with the
          journeys-grid card so opening a journey morphs the tile into this cover and closing the
          lightbox morphs back to it. Always mounted (even while loading) so the morph target is
          stable — see the heroSource/heroPlaceholder note above. */}
      <div className="journey-detail__cover">
        <JourneyReplayHero
          journeyId={journeyId}
          source={heroSource}
          taskHref={heroTaskHref}
          placeholder={heroPlaceholder}
          header={!embedded}
          title={journey?.name ?? 'Journey replay'}
        />
      </div>

      {embedded ? body : <div className="journey-detail__content">{body}</div>}
    </div>
  )
}
